<template>
  <div class="min-h-screen bg-[#f6f7f9]">

    <!-- ── Hero ─────────────────────────────────────────────────────────────── -->
    <section class="acties-hero relative overflow-hidden bg-[#FFD500] text-[#111]">
      <!-- Decoratieve %-motieven -->
      <span class="deco deco-1" aria-hidden="true">%</span>
      <span class="deco deco-2" aria-hidden="true">%</span>
      <i class="deco deco-bolt bi bi-lightning-charge-fill" aria-hidden="true"></i>

      <div class="max-w-screen-xl mx-auto px-6 py-16 lg:py-24 relative">
        <span class="inline-flex items-center gap-2 bg-black/10 px-3 py-1 rounded-[6px] text-[0.68rem] font-bold uppercase tracking-[0.1em] mb-5">
          <i class="bi bi-lightning-charge"></i> Acties
        </span>
        <h1 class="text-[clamp(2.5rem,7vw,4.5rem)] font-black leading-[0.95] mb-4">Acties &amp; Aanbiedingen</h1>
        <p class="text-[1.0625rem] lg:text-[1.25rem] text-black/70 max-w-[560px] leading-relaxed">
          Tijdelijk extra voordeel op geselecteerde thuisbatterijen en energieopslagsystemen. Op = op.
        </p>
      </div>
    </section>

    <!-- ── Trust-strip (hergebruikt) ────────────────────────────────────────── -->
    <HomeTrustBar />

    <div class="max-w-screen-xl mx-auto px-6 py-12 lg:py-16">

      <!-- ── Promo-blok: complete sets ──────────────────────────────────────── -->
      <router-link
        to="/categorie/thuisbatterijen"
        class="promo-card group relative flex flex-col justify-between overflow-hidden rounded-2xl bg-gradient-to-br from-[#111] to-[#333] text-white p-8 lg:p-10 mb-12 no-underline"
      >
        <span class="absolute top-5 right-6 text-[0.68rem] font-bold uppercase tracking-[0.1em] bg-[#FFD500] text-[#111] px-3 py-1 rounded-[6px]">Actie</span>
        <div>
          <h2 class="text-[1.5rem] lg:text-[2rem] font-black leading-tight mb-2 max-w-[520px] text-white">Plug &amp; Play Thuisbatterijen</h2>
          <p class="text-white/70 text-[0.95rem] mb-6 max-w-[480px]">Zelf aansluiten in 30 minuten. Bespaar tot <strong class="text-[#FFD500]">€300,-</strong> op geselecteerde Hoymiles HiBattery modellen.</p>
        </div>
        <span class="inline-flex items-center gap-2 self-start bg-[#FFD500] text-[#111] font-bold text-[0.9rem] px-5 py-3 rounded-lg group-hover:translate-x-0.5 transition-transform">
          Bekijk de aanbiedingen <i class="bi bi-arrow-right"></i>
        </span>
      </router-link>

      <!-- ── Grid van afgeprijsde producten ─────────────────────────────────── -->
      <div class="flex items-end justify-between mb-6">
        <h2 class="text-[1.5rem] lg:text-[1.875rem] font-black text-[#111]">Alle aanbiedingen</h2>
        <span v-if="!loading && deals.length" class="text-[0.85rem] text-[#888]">{{ deals.length }} product{{ deals.length !== 1 ? 'en' : '' }} in de aanbieding</span>
      </div>

      <!-- Loading skeletons -->
      <div v-if="loading" class="grid gap-3 grid-cols-2 lg:grid-cols-4" aria-hidden="true">
        <div v-for="n in 8" :key="n" class="bg-white rounded-[14px] border border-gray-100 h-[340px] animate-pulse"></div>
      </div>

      <!-- Producten -->
      <div v-else-if="deals.length" class="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <ProductCard
          v-for="(product, i) in deals"
          :key="product.id"
          :product="product"
          :index="i"
          list-name="Acties"
        />
      </div>

      <!-- Leeg -->
      <div v-else class="bg-white rounded-2xl border border-[#e9eaec] p-12 text-center">
        <div class="w-16 h-16 rounded-full bg-[#fffbe6] flex items-center justify-center mx-auto mb-4">
          <i class="bi bi-tag text-[1.75rem] text-[#FFD500]"></i>
        </div>
        <h3 class="text-[1.25rem] font-bold text-[#111] mb-2">Op dit moment geen actieve aanbiedingen</h3>
        <p class="text-[#888] mb-6 max-w-[420px] mx-auto">Kom binnenkort terug, of bekijk ons volledige assortiment - daar vind je altijd scherpe prijzen.</p>
        <router-link to="/producten" class="inline-flex items-center gap-2 bg-[#FFD500] hover:bg-[#ffb800] text-[#111] font-bold px-6 py-3 rounded-lg no-underline transition-colors">
          <i class="bi bi-grid"></i> Bekijk alle producten
        </router-link>
      </div>
    </div>

    <!-- ── CTA (hergebruikt) ────────────────────────────────────────────────── -->
    <HomeCtaBanner />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useProductStore } from '../stores/products.js'
import ProductCard from '@components/ProductCard.vue'
import HomeTrustBar from '@components/HomeTrustBar.vue'
import HomeCtaBanner from '@components/HomeCtaBanner.vue'

const store   = useProductStore()
const loading = ref(true)
const deals   = ref([])

onMounted(async () => {
  try {
    await store.fetchAllProducts()
    // Afgeprijsde producten: WooCommerce zet on_sale wanneer er een sale_price is
    deals.value = store.allProducts.filter(p => p.on_sale || (p.sale_price && p.sale_price !== p.regular_price))
  } catch (e) {
    // stil falen - de lege staat vangt dit op
  } finally {
    loading.value = false
  }
})
</script>

<style scoped>
.acties-hero {
  background-image:
    radial-gradient(60% 80% at 85% 0%, rgba(0,0,0,0.06), transparent 60%),
    radial-gradient(50% 70% at 0% 100%, rgba(255,255,255,0.25), transparent 60%);
}
.deco {
  position: absolute;
  color: rgba(0,0,0,0.07);
  line-height: 1;
  pointer-events: none;
  user-select: none;
  font-weight: 900;
}
.deco-1 { font-size: 20rem; top: -3rem;  right: 4rem; }
.deco-2 { font-size: 12rem; bottom: -3rem; right: 22rem; }
.deco-bolt { font-size: 9rem; top: 3rem; right: -1rem; color: rgba(0,0,0,0.06); }
@media (max-width: 768px) {
  .deco-1 { font-size: 11rem; right: -1rem; }
  .deco-2, .deco-bolt { display: none; }
}
</style>
